// TeamsToNextStageModal.jsx
import React, {useEffect, useMemo, useState} from "react";
import {Form} from "react-router-dom";
import Checkbox from "../common/Checkbox";
import AddTeamToStageMutation from "./mutation/AddTeamToStage";
import {GlobalStyles} from "../../constants/styles";
import useThemeColors from "../organization/portal/useThemeColors";
import {alphaFromHex} from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

export default function TeamsToNextStageModal({teamsToNextStage, stageId, sportKey}) {
    const [isCheckAll, setIsCheckAll] = useState(false);
    const [isCheck, setIsCheck] = useState([]);
    const [addTeamToStageMutation] = AddTeamToStageMutation();
    const colors = useThemeColors();
    const {t} = useTranslation();

    // Scoped token to avoid collisions
    const scopeToken = useMemo(
        () => `kbola_ttns_${String(stageId ?? "x").replace(/[^a-zA-Z0-9_-]/g, "_")}`,
        [stageId]
    );
    const scopeSel = `#teams-to-next-stage-modal[data-kbola-ttns="${scopeToken}"]`;

    // ✅ Header/Footer style tokens (same system as AddStageModal)
    const ui = useMemo(() => {
        return {
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {display: "flex", alignItems: "center", gap: 10, minWidth: 0},
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            closeBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: colors.textOnPrimary,
            },
            footer: {
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                padding: "12px 14px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.01)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
            btnDisabled: {
                background: GlobalStyles.colors.manatee,
                border: `1px solid ${GlobalStyles.colors.manatee}`,
                color: "#fff",
                cursor: "not-allowed",
                opacity: 0.9,
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    useEffect(() => {
        setIsCheck([
            ...teamsToNextStage.filter((li) => li.qualification !== "no").map((li) => li.teamId),
        ]);
    }, [teamsToNextStage]);

    const handleSelectAll = () => {
        setIsCheckAll((v) => !v);
        if (!isCheckAll) setIsCheck(teamsToNextStage.map((li) => li.teamId));
        else setIsCheck([]);
    };

    const handleClick = (e) => {
        const {id, checked} = e.target;
        const teamId = parseInt(id, 10);
        if (checked) setIsCheck((prev) => [...prev, teamId]);
        else setIsCheck((prev) => prev.filter((x) => x !== teamId));
    };

    const handleMoveTeams = () => {
        const selectedTeams = teamsToNextStage.filter((li) => isCheck.includes(li.teamId));
        addTeamToStageMutation({
            variables: {
                stageId,
                teamIds: selectedTeams.map((team) => team.teamId),
            },
        })
            .then((res) => {
                if (res.data?.addTeamToStage?.success) window.location.reload();
            })
            .catch(() => {
            });
    };

    const disabledSubmit = isCheck.length === 0;

    const getQualUI = (q) => {
        const v = String(q || "").toLowerCase();
        if (v === "qualified") return {label: "Qualified", pill: "success"};
        if (v === "additional") return {label: "Additional", pill: "warning"};
        if (v === "no") return {label: "Not qualified", pill: "muted"};
        return {label: "Not qualified", pill: "danger"};
    };

    const pct = (w, played) => {
        const p = Number(played || 0);
        if (!p) return "0.000";
        return (Number(w || 0) / p).toFixed(3);
    };

    const gb = (index, standing) => {
        if (index === 0) return "-";
        const leader = teamsToNextStage[0];
        return (
            ((Number(leader?.win || 0) - Number(standing?.win || 0)) +
                (Number(standing?.lose || 0) - Number(leader?.lose || 0))) /
            2
        ).toFixed(1);
    };

    const isBaseball = sportKey === "baseball" || sportKey === "softball";
    const isRugby = sportKey === "rugby";
    const translationKey = isBaseball ? "baseballStandings" : isRugby ? "rugbyStandings" : "soccerStandings";
    const statColumns = isBaseball
        ? [
            {label: t(`${translationKey}.columns.win`), value: (standing) => standing.win},
            {label: t(`${translationKey}.columns.lose`), value: (standing) => standing.lose},
            {label: t(`${translationKey}.columns.pct`), value: (standing) => pct(standing.win, standing.played)},
            {label: t(`${translationKey}.columns.gb`), value: (standing, index) => gb(index, standing)},
            {label: t(`${translationKey}.columns.rs`), value: (standing) => standing.scoreFor},
            {label: t(`${translationKey}.columns.ra`), value: (standing) => standing.scoreAgainst},
            {label: t(`${translationKey}.columns.diff`), value: (standing) => standing.scoreDifference},
        ]
        : isRugby
            ? [
                {label: t(`${translationKey}.columns.win`), value: (standing) => standing.win},
                {label: t(`${translationKey}.columns.draw`), value: (standing) => standing.draw},
                {label: t(`${translationKey}.columns.lose`), value: (standing) => standing.lose},
                {label: t(`${translationKey}.columns.pointsFor`), value: (standing) => standing.scoreFor},
                {label: t(`${translationKey}.columns.pointsAgainst`), value: (standing) => standing.scoreAgainst},
                {label: t(`${translationKey}.columns.difference`), value: (standing) => standing.scoreDifference},
                {label: t(`${translationKey}.columns.points`), value: (standing) => standing.points},
            ]
            : [
                {label: t(`${translationKey}.columns.win`), value: (standing) => standing.win},
                {label: t(`${translationKey}.columns.draw`), value: (standing) => standing.draw},
                {label: t(`${translationKey}.columns.lose`), value: (standing) => standing.lose},
                {label: t(`${translationKey}.columns.goalsFor`), value: (standing) => standing.scoreFor},
                {label: t(`${translationKey}.columns.goalsAgainst`), value: (standing) => standing.scoreAgainst},
                {label: t(`${translationKey}.columns.goalDifference`), value: (standing) => standing.scoreDifference},
                {label: t(`${translationKey}.columns.points`), value: (standing) => standing.points},
            ];

    return (
        <div
            className="modal fade"
            id="teams-to-next-stage-modal"
            data-kbola-ttns={scopeToken}
            tabIndex="-1"
            role="dialog"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-lg" role="document">
                <div className="modal-content" style={{borderRadius: 18, overflow: "hidden"}}>
                    <Form
                        method="post"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleMoveTeams();
                        }}
                    >
                        {/* ✅ Header (AddStageModal style) */}
                        <div style={ui.header}>
                            <div style={ui.titleWrap}>
                                <span style={ui.badge}>Next</span>
                                <div style={{minWidth: 0}}>
                                    <h4 style={ui.title}>Move teams to next stage</h4>
                                    <p style={ui.sub}>Select the teams that should advance.</p>
                                </div>
                            </div>

                            <button
                                type="button"
                                style={ui.closeBtn}
                                data-dismiss="modal"
                                aria-label="Close"
                                title="Close"
                            >
                                <i className="fas fa-times"/>
                            </button>
                        </div>

                        {/* Body (modern table version) */}
                        <div className="modal-body" style={{padding: 14}}>
                            <div className="kbola-ttns-card">
                                <div className="kbola-ttns-tableWrap">
                                    <table className="table kbola-ttns-table">
                                        <thead>
                                        <tr>
                                            <th className="kbola-ttns-checkCol">
                                                <Checkbox
                                                    type="checkbox"
                                                    name="Select All"
                                                    id="selectAll"
                                                    handleClick={handleSelectAll}
                                                    isChecked={isCheckAll}
                                                />
                                            </th>
                                            <th>{t(`${translationKey}.columns.team`)}</th>
                                            <th>Group</th>
                                            {statColumns.map((column) => (
                                                <th key={column.label} className="text-center">{column.label}</th>
                                            ))}
                                            <th>Status</th>
                                        </tr>
                                        </thead>

                                        <tbody>
                                        {teamsToNextStage.map((standing, index) => {
                                            const qual = getQualUI(standing.qualification);
                                            const isSelected = isCheck.includes(standing.teamId);

                                            return (
                                                <tr
                                                    key={standing.teamId ?? index}
                                                    className={isSelected ? "is-selected" : ""}
                                                >
                                                    <td className="kbola-ttns-checkCol">
                                                        <Checkbox
                                                            type="checkbox"
                                                            // name={standing.teamName}
                                                            id={standing.teamId}
                                                            handleClick={handleClick}
                                                            isChecked={isSelected}
                                                        />
                                                    </td>

                                                    <td>
                                                        <div className="kbola-ttns-teamCell">
                                                            <div
                                                                className="text-left">{standing.teamName}</div>
                                                        </div>
                                                    </td>

                                                    <td>
                              <span className="kbola-ttns-pill kbola-ttns-pillBrand">
                                {standing.groupName || "—"}
                              </span>
                                                    </td>

                                                    {statColumns.map((column) => (
                                                        <td key={column.label} className="text-center">
                                                            {column.value(standing, index)}
                                                        </td>
                                                    ))}

                                                    <td>
                              <span className={`kbola-ttns-pill kbola-ttns-pill-${qual.pill}`}>
                                {qual.label}
                              </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* ✅ Footer (AddStageModal style) */}
                        <div className="modal-footer" style={ui.footer}>
                            <button type="button" style={ui.btn} data-dismiss="modal">
                                <i className="fas fa-times"/>
                                Close
                            </button>

                            <button
                                type="submit"
                                disabled={disabledSubmit}
                                style={{
                                    ...ui.btn,
                                    ...ui.btnPrimary,
                                    ...(disabledSubmit ? ui.btnDisabled : {}),
                                }}
                                title={disabledSubmit ? "Select at least one team" : "Move selected teams"}
                            >
                                <i className="fas fa-arrow-right"/>
                                Move teams
                            </button>
                        </div>
                    </Form>
                </div>
            </div>

            {/* Scoped styles for the modern table + focus polish */}
            <style>{`
        ${scopeSel} { font-family: 'Poppins', sans-serif; }

        ${scopeSel} .kbola-ttns-card {
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 16px;
          overflow: hidden;
          background: #fff;
        }

        ${scopeSel} .kbola-ttns-tableWrap {
          overflow: auto;
        }

        ${scopeSel} .kbola-ttns-table {
          margin: 0;
        }

        ${scopeSel} .kbola-ttns-table thead th {
          position: sticky;
          top: 0;
          background: ${colors.primary};
          color: ${colors.textOnPrimary};
          font-weight: 900;
          font-size: 12px;
          border-bottom: 1px solid rgba(0,0,0,0.08);
          white-space: nowrap;
          z-index: 1;
        }

        ${scopeSel} .kbola-ttns-table td {
          vertical-align: middle;
          border-top: 1px solid rgba(0,0,0,0.06);
          font-weight: 800;
          font-size: 12px;
          color: rgba(20,21,26,0.78);
          white-space: nowrap;
        }

        ${scopeSel} .kbola-ttns-checkCol {
          width: 44px;
        }

        ${scopeSel} .kbola-ttns-teamName {
          font-weight: 900;
          color: ${colors.primary};
          max-width: 240px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        ${scopeSel} tr.is-selected td {
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.10);
        }

        ${scopeSel} .kbola-ttns-pill {
          display: inline-flex;
          align-items: center;
          padding: 6px 10px;
          border-radius: 999px;
          font-weight: 900;
          font-size: 11px;
          border: 1px solid rgba(0,0,0,0.10);
          line-height: 1;
          white-space: nowrap;
        }

        ${scopeSel} .kbola-ttns-pillBrand {
          background: ${colors.secondary};
          color: ${colors.textOnSecondary};
        }

        ${scopeSel} .kbola-ttns-pill-success {
          background: rgba(25,135,84,0.10);
          color: rgba(25,135,84,0.95);
          border-color: rgba(25,135,84,0.18);
        }

        ${scopeSel} .kbola-ttns-pill-warning {
          background: rgba(255,193,7,0.14);
          color: rgba(161,98,7,0.95);
          border-color: rgba(255,193,7,0.22);
        }

        ${scopeSel} .kbola-ttns-pill-danger {
          background: rgba(220,53,69,0.10);
          color: rgba(220,53,69,0.95);
          border-color: rgba(220,53,69,0.18);
        }

        ${scopeSel} .kbola-ttns-pill-muted {
          background: rgba(0,0,0,0.03);
          color: rgba(20,21,26,0.60);
          border-color: rgba(0,0,0,0.08);
        }

        /* Focus polish */
        ${scopeSel} .modal-content button:focus,
        ${scopeSel} .modal-content input:focus,
        ${scopeSel} .modal-content select:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
        }
      `}</style>
        </div>
    );
}
